/**
 * Import PDF Tool
 *
 * Imports PDF files as underlay/reference. Opens a real file picker on
 * activation (BIM: Import PDF dialog); the underlay is then placed at the
 * clicked point.
 */

import { v4 as uuidv4 } from 'uuid';
import { LinkElement, LinkGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { LinkManager } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import i18n from '../i18n';

export class ImportPDFTool extends BaseTool {
    id = 'import_pdf';
    name = 'Import PDF';
    cursor = 'alias';

    private linkManager: LinkManager;
    private pickedFile: File | null = null;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    onActivate(): void {
        // Dialog-driven command: open the file picker immediately (BIM:
        // Import PDF dialog). Placement completes on the next click.
        void this.pickFile('.pdf').then((file) => {
            if (file) {
                this.pickedFile = file;
                this.notify(i18n.t('tools.notify.pdfSelected', { fileName: file.name }));
            } else {
                this.exitToSelect();
            }
        });
    }

    public executeImport(filePath: string, origin?: { x: number; y: number; z: number }, options: {
        resolution?: number;
        pageNumber?: number;
        size?: number;
        underlayUrl?: string;
    } = {}) {
        if (!this.context) return;

        const resolution = options.resolution || 300; // DPI
        const pageNumber = options.pageNumber || 1;
        const ox = origin?.x ?? 0;
        const oy = origin?.y ?? 0;
        const oz = origin?.z ?? 0;

        // 1. Create Link Instance (imported content is still tracked so the
        // page can be re-placed; status reflects it is embedded, not linked).
        const linkInstance = this.linkManager.linkFile('PDF', filePath, {
            positioning: 'OriginToOrigin',
            size: options.size ?? 0
        });

        // 2. Create Wrapper Element centered on the picked point
        const geometryData: LinkGeometryData = {
            linkId: linkInstance.id,
            origin: [ox, oy, oz],
            scale: [1, 1, 1],
            rotation: [0, 0, 0],
            boxSize: [8.5 * 0.0254, 11 * 0.0254, 0.001] // Letter size in meters
        };

        const pdfElement: LinkElement = {
            id: uuidv4(),
            category: 'Other Links',
            type: 'Imported PDF: ' + linkInstance.name,
            parameters: {
                'File Path': filePath,
                'Resolution': `${resolution} DPI`,
                'Page': pageNumber.toString(),
                'Scale': '1:1',
                // Session blob: lets the underlay renderer show the page box
                // (vector rasterization needs a PDF engine not bundled here).
                ...(options.underlayUrl ? { 'Underlay URL': options.underlayUrl } : {}),
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(pdfElement);
        this.linkManager.setDocumentId(linkInstance.id, pdfElement.id);
        this.notify(i18n.t('tools.notify.pdfImported', { filePath: filePath }), 'success');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const file = this.pickedFile;
        this.executeImport(file?.name ?? 'Underlay.pdf', point, {
            size: file?.size,
            underlayUrl: file ? URL.createObjectURL(file) : undefined,
        });
        this.exitToSelect();
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }
}
